<template>
	<nav class="tabs tabs-main" :class="{ 'tabs-mini-gap' : miniGap }" @click.shift="dumpMeasurements">
		<template v-if="topMenu.sections.length > 0">
			<div class="tabs-primary" ref="primaryDiv">
				<one-tab v-for="section in primaryRow.tabs" :mi="section" :level="1" :active="isActiveSection(section)" :key="section.name" :minify="minimized(section)"></one-tab>
				<expand-tabs v-if="primaryRow.needExpander" @click="expandRow(primaryRow.parentName)"></expand-tabs>
			</div>
			<div class="tabs-secondary" v-if="navPath.length > 1" ref="secondaryDiv">
				<one-tab v-for="section in secondaryRow.tabs" :mi="section" :level="2" :active="isActiveSection(section)" :key="section.name" :minify="minimized(section)"></one-tab>
				<expand-tabs v-if="secondaryRow.needExpander" @click="expandRow(secondaryRow.parentName)"></expand-tabs>
			</div>
		</template>
	</nav>
</template>

<script setup lang="ts">
import type {MenuItem, TopMenu} from "@t/ui/Menu";
import {hasChildren, isAction, normalizeTopMenu, supportBadges} from "@t/ui/Menu";
import {computed, onMounted, ref, reactive, nextTick, watch} from "vue";
import { useRoute } from "vue-router";
import {findNavPath, classifyNavSection} from "@/router/Router";
import OneTab from "@/ui/mouse/OneTab.vue";
import ExpandTabs from "@/ui/mouse/ExpandTabs.vue";
import {injectLayoutWidth} from "@fd/LayoutWidth.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {storeToRefs} from "pinia";

const props = defineProps<{
	menuData?: TopMenu;
	noCollapsing?: boolean;
	/**
	 * parent can provide active section (navigation name)
	 * Active section can be determined (in this order):
	 * a) from this property
	 * b) from route meta info
	 * c) from route path
	 **/
	activeSection?: string;
}>();

const route = useRoute();

const navStore = useNavigationStore();
navStore.setOrLoadTopMenu(props.menuData);
const { topMenu } = storeToRefs(navStore);

const expanded = reactive(new Set());
const primaryDiv = ref<HTMLDivElement|null>(null);
const secondaryDiv = ref<HTMLDivElement|null>(null);
const miniGap = ref<boolean>(false);

interface ItemWidth {
	max: number;	// maximum width of the item on the screen
	min: number;	// minimum possible width of the item
	minimized: boolean;		// is currently minimized
}

const widths = ref<Map<string, ItemWidth>>(new Map());
const screen = injectLayoutWidth();

function dumpMeasurements() {
	const w = widths.value;
	for (let wk of w.keys()) {
		const m = w.get(wk);
		console.log(wk + " = " + m.min+"/"+m.max+" "+(m.minimized ? "M":"-"));
	}
}

function expandRow(parentName: string):void {
	expanded.add(parentName);
}

function isExpanded(item: MenuItem, parentName: string) {
	if (props.noCollapsing) return true;
	return !item.collapsed || expanded.has(parentName) || isActiveSection(item);
}

const primaryRow = computed(() => {
	let needExpander = false;
	const tabs:MenuItem[] = [];
	const parentName = '_root_';
	for (let section of topMenu.value.sections) {
		const ex = isExpanded(section, parentName);
		if (ex) tabs.push(section);
		needExpander = needExpander || !ex;
	}
	return { tabs, needExpander, parentName };
});

const secondaryRow = computed(() => {
	let needExpander = false;
	const tabs:MenuItem[] = [];
	const primary = navPath.value[0];
	const parentName = primary.name;
	if (navPath.value.length > 1 && hasChildren(primary)) {
		for (let section of primary.children) {
			const ex = isExpanded(section, parentName);
			if (ex) tabs.push(section);
			needExpander = needExpander || !ex;
		}
	}
	return { tabs, needExpander, parentName };
});

const navPath = computed<MenuItem[]>(() => {
	const menuItems = findNavPath(topMenu.value.sections, route, props.activeSection);
	return menuItems;
});

function isActiveSection(mi:MenuItem):boolean {
	const cls = classifyNavSection(navPath.value, mi);
	return cls !== "";
}

function measureTabsRow(el: HTMLDivElement) {
	const links = el.querySelectorAll("a");
	const namesMesured:string[] = [];
	for (let link of links) {
		// Does the link have a name?
		const n = link.dataset.name;
		if (n) {
			const existingMeasurement = widths.value.get(n);
			if (existingMeasurement && existingMeasurement.minimized) continue;	// Do not measure minimized items
			const w = link.getBoundingClientRect().width;
			const measure:ItemWidth = {
				max: w,
				min: w,
				minimized: false
			}
			const icon = link.querySelector(".fi");	// icon inside the link
			if (icon) {
				measure.min = icon.getBoundingClientRect().width;
			}
			widths.value.set(n, measure);
			namesMesured.push(n);
		}
	}
	//console.log("Measured row: "+namesMesured.join("|"));
}

function minimized(item:MenuItem):boolean {
	if (item.name) {
		const w = widths.value.get(item.name);
		if (w) return w.minimized;
	}
	return false;
}

function measureTabs() {
	//console.log("Measuring Tabs ....");
	if (primaryDiv.value) measureTabsRow(primaryDiv.value);
	if (secondaryDiv.value) measureTabsRow(secondaryDiv.value);
	calcMinimized(screen.value);
}

function calcMinimizedFor(screen:number, tabs:MenuItem[]):boolean {
	// calc primary row
	let need:number = 8;	// start with minimal gap (on the right)
	for (let t of tabs) {
		if (t.name) {
			const msr = widths.value.get(t.name);
			if (!msr) console.warn("No measurement for " + t.name);
			need += msr.max + 16;	// plus gap
		} else console.warn("Tab with no name: "+JSON.stringify(t));
	}
	let reducedTo = need;
	const namesCalculated:string[] = [];
	for (let i = tabs.length - 1; i >= 0; i--) {
		const t:MenuItem = tabs[i];
		const msr = widths.value.get(t.name);
		if (reducedTo > screen) {
			msr.minimized = true;
			reducedTo = reducedTo - msr.max + msr.min;
			//console.log("  "+t.name)
			namesCalculated.push(t.name.toUpperCase());
		} else {
			msr.minimized = false;
			namesCalculated.push(t.name);
		}
	}
	//console.log("Need " + need + " but screen is " + screen + " for "+namesCalculated.join("|"));
	return reducedTo > screen;	// not enough
}

function calcMinimized(screen:number):void {
	if (topMenu.value.sections.length > 0) {
		const primaryNeedsReducedGap = calcMinimizedFor(screen, primaryRow.value.tabs);
		const secondaryNeedsReducedGap = navPath.value.length > 1 ? calcMinimizedFor(screen, secondaryRow.value.tabs) : false;
		miniGap.value = primaryNeedsReducedGap || secondaryNeedsReducedGap;
	}
}

function dumpNavPath():string {
	let b: string[] = [];
	navPath.value.forEach(mi => b.push(mi.name));
	return b.join(" / ");
}

watch(() => screen.value, (s) => calcMinimized(s));
watch([topMenu, navPath], () => {
	//console.log("Measure top menu on change");
	measureTabs();
}, { flush: "post", deep: true });

onMounted(() => {
	//console.log("Measure top menu onMounted");
	measureTabs();
});
</script>

<style>
.navshow-enter-active,
.navshow-leave-active {
	transition: opacity 750ms ease;
}
.navshow-enter-to,
.navshow-leave-from {
	opacity: 1;
}
.navshow-enter-from,
.navshow-leave-to {
	opacity: 0;
}
</style>
